import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import LoadingDots from '../components/LoadingDots'
import { updatePassword } from '../lib/api'
import { supabase } from '../lib/supabase'

type Status = 'verifying' | 'ready' | 'done' | 'invalid'

export default function ResetPassword() {
  const navigate = useNavigate()

  const [status, setStatus] = useState<Status>('verifying')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setStatus('ready')
    })

    // getSession() resolves after the client has processed any recovery token
    // in the URL — a session means the link was valid (or the user is already
    // logged in and just wants to change their password)
    supabase.auth.getSession().then(({ data }) => {
      setStatus((s) => (s === 'ready' || s === 'done')
        ? s
        : data.session ? 'ready' : 'invalid')
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (status !== 'done') return
    const timer = setTimeout(() => navigate('/manage', { replace: true }), 2000)
    return () => clearTimeout(timer)
  }, [status, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passordene stemmer ikke overens.')
      return
    }

    setSubmitting(true)
    try {
      await updatePassword(password)
      setStatus('done')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  if (status === 'verifying') {
    return <div className="min-h-dvh bg-gray-950" />
  }

  return (
    <div className="min-h-dvh bg-gray-950 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm flex flex-col gap-8">
        <h1 className="text-white text-4xl font-bold tracking-tight text-center">h3r</h1>

        {status === 'invalid' && (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl bg-red-900 border border-red-700 px-5 py-4 text-red-200 text-sm text-center leading-relaxed">
              Lenken er ugyldig eller utløpt. Be om en ny lenke for tilbakestilling.
            </div>
            <Link
              to="/login"
              className="text-sm text-blue-400 hover:text-blue-300 text-center transition-colors"
            >
              Tilbake til innlogging
            </Link>
          </div>
        )}

        {status === 'done' && (
          <div className="rounded-xl bg-green-900 border border-green-700 px-5 py-4 text-green-200 text-sm text-center leading-relaxed">
            Passordet er oppdatert. Du sendes videre …
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-sm text-gray-400">Nytt passord</label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="
                  bg-gray-900 border border-gray-700 rounded-lg
                  px-3 py-2.5 text-base text-white
                  focus:outline-none focus:border-blue-500
                  placeholder-gray-600
                "
                placeholder="Minst 8 tegn"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-sm text-gray-400">Bekreft passord</label>
              <input
                id="confirm-password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="
                  bg-gray-900 border border-gray-700 rounded-lg
                  px-3 py-2.5 text-base text-white
                  focus:outline-none focus:border-blue-500
                  placeholder-gray-600
                "
                placeholder="Gjenta passordet"
              />
            </div>

            {error && (
              <div className="rounded-lg bg-red-900 border border-red-700 px-4 py-3 text-red-200 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="
                flex items-center justify-center gap-2
                bg-blue-600 hover:bg-blue-500
                disabled:bg-gray-700 disabled:cursor-not-allowed
                text-white text-sm font-medium
                px-4 py-2.5 rounded-lg
                transition-colors duration-200 active:scale-95
              "
            >
              {submitting && (
                <LoadingDots />
              )}
              Sett nytt passord
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
